<template>
  <GeojsonCustomLayer :before="beforeLayerId" :layer="layer" />
</template>

<script>
import mapboxgl from 'mapbox-gl'
import GLTFLoader from 'three-gltf-loader'
import {
  AmbientLight,
  Camera,
  DirectionalLight,
  Matrix4,
  Scene,
  Vector3,
  WebGLRenderer
} from 'three'
import GeojsonCustomLayer from '@/components/map/mapbox-layers/GeojsonCustomLayer.vue'

export default {
  name: 'LayerModelGeneral',
  components: {
    GeojsonCustomLayer
  },
  props: {
    sourceId: {
      type: String,
      default: ''
    },
    layerId: {
      type: String,
      required: true
    },
    beforeLayerId: {
      type: String,
      default: '',
      required: false
    },
    sourceUrl: {
      type: String,
      required: true
    },
    altitude: {
      type: Number,
      required: true
    },
    origin: {
      type: Array,
      required: true
    }
  },
  emits: ['loadingModelDone'],
  data() {
    return {
      modelRotate: [Math.PI / 2, 0, 0]
    }
  },
  computed: {
    modelAsMercatorCoordinate() {
      return mapboxgl.MercatorCoordinate.fromLngLat(this.origin, this.altitude)
    },
    layer() {
      const thisVue = this
      return {
        id: this.layerId,
        type: 'custom',
        renderingMode: '3d',
        map: {},
        modelTransform: {
          translateX: this.modelAsMercatorCoordinate.x,
          translateY: this.modelAsMercatorCoordinate.y,
          translateZ: this.modelAsMercatorCoordinate.z,
          rotateX: this.modelRotate[0],
          rotateY: this.modelRotate[1],
          rotateZ: this.modelRotate[2],
          /* Since our 3D model is in real world meters, a scale transform needs to be
           * applied since the CustomLayerInterface expects units in MercatorCoordinates.
           */
          scale: this.modelAsMercatorCoordinate.meterInMercatorCoordinateUnits()
        },
        source: this.sourceUrl,
        onAdd: function (map, gl) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.map = map
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.camera = new Camera()
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.scene = new Scene()

          // create three.js lights to illuminate the model
          const directionalLight = new DirectionalLight(0xf5f8ff, 1)
          const directionalLight2 = new DirectionalLight(0xf5f8ff, 1)
          const ambientLight = new AmbientLight(0x404040, 0.7)
          directionalLight.position.set(-1, 1, -1).normalize()
          directionalLight2.position.set(1, 1, 1).normalize()
          this.scene.add(directionalLight, directionalLight2, ambientLight)

          // use the three.js GLTF loader to add the 3D model to the three.js scene
          const loader = new GLTFLoader()
          loader.load(
            this.source,
            function (gltf) {
              this.scene.add(gltf.scene)
            }.bind(this),
            (ins) => {
              if (ins.total === ins.loaded) thisVue.$emit('loadingModelDone', 'GENERAL')
            }
          )

          // use the Mapbox GL JS map canvas for three.js
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.renderer = new WebGLRenderer({
            canvas: map.getCanvas(),
            context: gl,
            antialias: true
          })

          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.renderer.autoClear = false
        },
        render: function (gl, matrix) {
          const rotationX = new Matrix4().makeRotationAxis(
            new Vector3(1, 0, 0),
            this.modelTransform.rotateX
          )
          const rotationY = new Matrix4().makeRotationAxis(
            new Vector3(0, 1, 0),
            this.modelTransform.rotateY
          )
          const rotationZ = new Matrix4().makeRotationAxis(
            new Vector3(0, 0, 1),
            this.modelTransform.rotateZ
          )

          const m = new Matrix4().fromArray(matrix)
          const l = new Matrix4()
            .makeTranslation(
              this.modelTransform.translateX,
              this.modelTransform.translateY,
              this.modelTransform.translateZ
            )
            .scale(
              new Vector3(
                this.modelTransform.scale,
                -this.modelTransform.scale,
                this.modelTransform.scale
              )
            )
            .multiply(rotationX)
            .multiply(rotationY)
            .multiply(rotationZ)

          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.camera.projectionMatrix = m.multiply(l)
          this.renderer.state.reset()
          this.renderer.render(this.scene, this.camera)
          this.map.triggerRepaint()
        }
      }
    }
  }
}
</script>
